// /src/Components/Domain/components/DeploymentPipeline.jsx

import React from "react";
import {
  CheckCircle2,
  FileJson,
  Globe2,
  Layers3,
  Loader2,
  Server,
  TerminalSquare,
  XCircle,
} from "lucide-react";

export default function DeploymentPipeline({
  creating,
  deploymentSteps,
  terminalText,
  dynamicHost,
  selectedTemplateCode,
  selectedClientId,
}) {
  const completed =
    deploymentSteps?.dns === "done" &&
    deploymentSteps?.cpanel === "done" &&
    deploymentSteps?.theme === "done" &&
    deploymentSteps?.manifest === "done";

  const steps = [
    {
      key: "dns",
      icon: <Globe2 size={18} />,
      idleTitle: "DNS Setup",
      runningTitle: "Creating DNS",
      doneTitle: "DNS Created",
      errorTitle: "DNS Failed",
      idleText: "DNS record will be created.",
      runningText: `Writing A record for ${dynamicHost}`,
      doneText: "DNS record and cloud routing are ready.",
      errorText: "DNS creation failed. Please retry.",
    },
    {
      key: "cpanel",
      icon: <Server size={18} />,
      idleTitle: "cPanel Setup",
      runningTitle: "Creating cPanel",
      doneTitle: "cPanel Created",
      errorTitle: "cPanel Failed",
      idleText: "Hosting workspace will be provisioned.",
      runningText: "Creating subdomain directory and workspace.",
      doneText: "cPanel workspace created successfully.",
      errorText: "cPanel creation failed. Please retry.",
    },
    {
      key: "theme",
      icon: <Layers3 size={18} />,
      idleTitle: "Theme Setup",
      runningTitle: "Implementing Theme",
      doneTitle: "Theme Implemented",
      errorTitle: "Theme Failed",
      idleText: "Selected template files will be installed.",
      runningText: `Installing ${selectedTemplateCode || "theme bundle"}`,
      doneText: "Template files copied successfully.",
      errorText: "Theme implementation failed. Please retry.",
    },
    {
      key: "manifest",
      icon: <FileJson size={18} />,
      idleTitle: "Manifest Sync",
      runningTitle: "Synchronizing JSON",
      doneTitle: "Manifest Synced",
      errorTitle: "Automatic Sync Failed",
      idleText: "Deployment manifest will be synchronized.",
      runningText: `Binding JSON with ${
        selectedClientId || "selected client"
      }`,
      doneText: "Manifest synchronized successfully.",
      errorText:
        "Automatic sync failed after 2 attempts. Use Manual JSON Update from Websites after some time.",
    },
  ];

  const completedCount = Object.values(deploymentSteps || {}).filter(
    (status) => status === "done",
  ).length;

  const hasRunningStep = Object.values(deploymentSteps || {}).some(
    (status) => status === "running",
  );

  const progressPercentage = completed
    ? 100
    : Math.min(
        95,
        completedCount * 25 + (hasRunningStep ? 12 : 0),
      );

  return (
    <div className="mt-5 overflow-hidden rounded-2xl border bg-white">
      {/* Deployment console header */}
      <div className="border-b bg-gray-950 px-4 py-3 text-white">
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <TerminalSquare
                size={17}
                className="text-emerald-400"
              />
              Deployment Console
            </div>

            <p className="mt-1 font-mono text-xs text-emerald-300">
              {terminalText}
            </p>
          </div>

          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold">
            {creating ? (
              <>
                <Loader2
                  size={13}
                  className="animate-spin text-emerald-400"
                />
                Running
              </>
            ) : completed ? (
              <>
                <CheckCircle2
                  size={13}
                  className="text-emerald-400"
                />
                Completed
              </>
            ) : (
              <>
                <TerminalSquare
                  size={13}
                  className="text-gray-400"
                />
                Ready
              </>
            )}
          </div>
        </div>

        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-emerald-400 transition-all duration-500"
            style={{
              width: `${progressPercentage}%`,
            }}
          />
        </div>
      </div>

      {/* Four deployment cards */}
      <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-2 xl:grid-cols-4">
        {steps.map((step) => (
          <DeploymentStepCard
            key={step.key}
            step={step}
            status={deploymentSteps?.[step.key] || "idle"}
          />
        ))}
      </div>
    </div>
  );
}

function DeploymentStepCard({ step, status }) {
  const isRunning = status === "running";
  const isDone = status === "done";
  const isError = status === "error";

  const title = isDone
    ? step.doneTitle
    : isRunning
      ? step.runningTitle
      : isError
        ? step.errorTitle
        : step.idleTitle;

  const text = isDone
    ? step.doneText
    : isRunning
      ? step.runningText
      : isError
        ? step.errorText
        : step.idleText;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border p-4 transition-all ${
        isDone
          ? "border-emerald-200 bg-emerald-50"
          : isRunning
            ? "border-emerald-300 bg-white shadow-md shadow-emerald-100"
            : isError
              ? "border-rose-200 bg-rose-50"
              : "border-gray-200 bg-white"
      }`}
    >
      {isRunning && (
        <div className="absolute inset-x-0 top-0 h-1 animate-pulse bg-emerald-400" />
      )}

      <div className="flex items-start gap-3">
        <div
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
            isDone
              ? "bg-emerald-100 text-emerald-700"
              : isRunning
                ? "bg-emerald-50 text-emerald-600"
                : isError
                  ? "bg-rose-100 text-rose-600"
                  : "bg-gray-50 text-gray-500"
          }`}
        >
          {isDone ? (
            <CheckCircle2 size={20} />
          ) : isRunning ? (
            <Loader2 size={20} className="animate-spin" />
          ) : isError ? (
            <XCircle size={20} />
          ) : (
            step.icon
          )}
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">
              {title}
            </h4>

            {isRunning && (
              <StatusBadge label="Running" type="running" />
            )}

            {isDone && (
              <StatusBadge label="Done" type="done" />
            )}

            {isError && (
              <StatusBadge label="Failed" type="error" />
            )}
          </div>

          <p className="mt-1 text-xs leading-5 text-gray-500">
            {text}
          </p>

          {isRunning && (
            <div className="mt-3 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
              <span
                className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400"
                style={{ animationDelay: "120ms" }}
              />
              <span
                className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400"
                style={{ animationDelay: "240ms" }}
              />

              <span className="ml-1 font-mono text-[11px] text-gray-400">
                executing
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ label, type }) {
  const className =
    type === "done"
      ? "bg-emerald-100 text-emerald-700"
      : type === "error"
        ? "bg-rose-100 text-rose-700"
        : "bg-emerald-50 text-emerald-700";

  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${className}`}
    >
      {label}
    </span>
  );
}